import { React, useState, useEffect, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import Loading from "react-fullscreen-loading";
import { AuthContext } from '../../../component/context/AuthContext';
import axios from "axios";
import { toast } from "react-toastify";
import * as CONST from "../../../component/config/Constant"
import moment from 'moment';
import {
  IconCalendar, IconClock, IconMail, IconMessage,
  IconCalendarEvent, IconUser, IconBriefcase, IconUsers,
  IconCheck, IconX, IconArrowLeft
} from '@tabler/icons-react';
import '../employee/employee-details.css';

function ApproveLeave() {
  const [authUserData] = useContext(AuthContext);
  const [fullLoading, setFullLoading] = useState(true);
  const navigate = useNavigate();
  const params = useParams();
  const [comment, setComment] = useState('');
  const [leaveData, setLeaveData] = useState(null);
  const [disabled, setDisabled] = useState('');
  const leaveID = params.id || 0;

  let isToken = '';
  let userId = '';
  if (authUserData && authUserData[0]) {
    isToken = authUserData[0].token || "";
    userId = authUserData[0].user_id || 0;
  }

  const fetchLeaveDetail = async () => {
    try {
      const response = await axios.get(CONST.GET_LEAVE_DETAIL + leaveID, {
        headers: { 'Authorization': `Bearer ${isToken}` }
      });
      if (response.data.status === 'success') {
        setLeaveData(response.data.data);
      } else {
        toast.error("Leave not found");
        navigate('/admin/employee/leaves/all');
      }
    } catch (error) {
      toast.error("Failed to load leave details");
      navigate('/admin/employee/leaves/all');
    }
    setFullLoading(false);
  };

  useEffect(() => {
    if (isToken) fetchLeaveDetail();
  }, []);

  const handleAction = async (status) => {
    if (!comment.trim()) {
      toast.error("Please enter a comment before " + (status === 1 ? "approving" : "rejecting"));
      return;
    }
    setDisabled('disabled');
    try {
      const response = await axios.post(CONST.LEAVE_CHANGE_STATUS, {
        id: userId,
        leave_id: leaveID,
        leave_comment: comment,
        status: status,
      }, {
        headers: { 'Authorization': `Bearer ${isToken}` }
      });
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        navigate('/admin/employee/leaves/all');
      } else {
        toast.error(response.data.message);
        setDisabled('');
      }
    } catch (error) {
      toast.error("Something went wrong");
      setDisabled('');
    }
  };

  const formatTime = (t) => t ? t.slice(0, -3) : '00:00';

  const statusLabel = (s) => {
    if (s == 1) return { text: "APPROVED", bg: "#ecfdf5", color: "#059669" };
    if (s == 2) return { text: "REJECTED", bg: "#fef2f2", color: "#dc2626" };
    if (s == 3) return { text: "CANCELLED", bg: "#f1f5f9", color: "#94a3b8" };
    return { text: "PENDING", bg: "#fffbeb", color: "#d97706" };
  };

  if (fullLoading) {
    return <Loading loading={true} background="rgba(0, 0, 0, 0.5)" loaderColor="#fff" />;
  }

  if (!leaveData) return null;

  const st = statusLabel(leaveData.status);
  const isPending = leaveData.status == 0;

  return (
    <>
      <div className="page-header">
        <div className="container-xl">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <div className="page-pretitle">Leave Management</div>
              <h2 className="page-title">Leave Detail</h2>
            </div>
            <button className="btn btn-outline-primary" onClick={() => navigate(-1)}>
              <IconArrowLeft size={16} /> Back
            </button>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="row g-4">
            {/* Left: Employee & Leave Info */}
            <div className="col-lg-4">
              <div className="card" style={{ borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <div className="card-body text-center">
                  <div className="mb-3 mt-2">
                    {leaveData.user?.profile_photo_path ? (
                      <img
                        src={CONST.HOST + leaveData.user.profile_photo_path}
                        alt="Profile"
                        style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span className="d-inline-flex align-items-center justify-content-center"
                        style={{ width: 72, height: 72, borderRadius: '50%', background: '#0f2847', color: '#7db3ff', fontSize: '1.4rem', fontWeight: 700 }}>
                        {leaveData.user?.first_name?.[0]}{leaveData.user?.last_name?.[0]}
                      </span>
                    )}
                  </div>
                  <h4 style={{ fontWeight: 700, color: '#0a1628', marginBottom: 4 }}>
                    {leaveData.user?.first_name} {leaveData.user?.last_name}
                  </h4>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 16 }}>
                    {leaveData.user?.department?.name} / {leaveData.user?.designation?.name}
                  </p>

                  <div style={{ background: st.bg, color: st.color, display: 'inline-block', padding: '6px 18px', borderRadius: 20, fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.5px' }}>
                    {st.text}
                  </div>
                </div>
                <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px 20px' }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {[
                      { icon: <IconMail size={16} />, text: leaveData.user?.email },
                      { icon: <IconCalendarEvent size={16} />, text: `${leaveData.leave_type == 1 ? 'Full Day' : 'Half Day'} (${leaveData.e_leave_type?.name || ''})` },
                      { icon: <IconCalendar size={16} />, text: leaveData.leave_type == 1 ? `${moment(leaveData.leave_from_date).format("DD MMM YYYY")} to ${moment(leaveData.leave_to_date).format("DD MMM YYYY")}` : moment(leaveData.leave_from_date).format("DD MMM YYYY") },
                      { icon: <IconClock size={16} />, text: `${formatTime(leaveData.from_time)} - ${formatTime(leaveData.to_time)}` },
                      { icon: <IconUser size={16} />, text: `Applied: ${moment(leaveData.leave_applied_date).format("DD MMM YYYY HH:mm")}` },
                    ].map((item, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 4 ? '1px solid #f1f5f9' : 'none', fontSize: '0.85rem', color: '#334155' }}>
                        <span style={{ color: '#1167e8', minWidth: 16 }}>{item.icon}</span>
                        {item.text}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Right: Details + Action */}
            <div className="col-lg-8">
              {/* Recipients */}
              <div className="card mb-3" style={{ borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <div className="card-body">
                  <h6 style={{ fontWeight: 700, color: '#0a1628', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                    <IconUsers size={18} style={{ color: '#1167e8' }} /> Recipients
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {leaveData.e_leave_to_mails?.map((e, i) => (
                      <span key={`to-${i}`} style={{ background: '#f0f5ff', color: '#1167e8', border: '1px solid #dbeafe', padding: '4px 12px', borderRadius: 16, fontSize: '0.78rem', fontWeight: 600 }}>
                        To: {e.leaveToMailUserData?.first_name} {e.leaveToMailUserData?.last_name}
                      </span>
                    ))}
                    {leaveData.e_leave_cc_mails?.map((e, i) => (
                      <span key={`cc-${i}`} style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', padding: '4px 12px', borderRadius: 16, fontSize: '0.78rem', fontWeight: 600 }}>
                        CC: {e.leaveCcMailUserData?.first_name} {e.leaveCcMailUserData?.last_name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Subject & Reason */}
              <div className="card mb-3" style={{ borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <div className="card-body">
                  <h6 style={{ fontWeight: 700, color: '#0a1628', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <IconMail size={18} style={{ color: '#1167e8' }} /> Subject
                  </h6>
                  <p style={{ fontSize: '0.92rem', fontWeight: 600, color: '#0a1628', marginBottom: 16 }}>{leaveData.subject}</p>

                  <h6 style={{ fontWeight: 700, color: '#0a1628', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <IconMessage size={18} style={{ color: '#1167e8' }} /> Reason
                  </h6>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', fontSize: '0.88rem', color: '#334155', lineHeight: 1.6 }}>
                    <span dangerouslySetInnerHTML={{ __html: leaveData.reason }} />
                  </div>
                </div>
              </div>

              {/* Existing Comment */}
              {leaveData.leave_comment && (
                <div className="card mb-3" style={{ borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                  <div className="card-body">
                    <h6 style={{ fontWeight: 700, color: '#0a1628', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <IconMessage size={18} style={{ color: '#1167e8' }} /> Reviewer Comment
                    </h6>
                    <div style={{ background: '#f8fafc', borderLeft: '3px solid #1167e8', borderRadius: '0 8px 8px 0', padding: '12px 16px', fontSize: '0.88rem', color: '#334155' }}>
                      {leaveData.leave_comment}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Section — only for pending */}
              {isPending && (
                <div className="card" style={{ borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                  <div className="card-body">
                    <h6 style={{ fontWeight: 700, color: '#0a1628', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                      <IconMessage size={18} style={{ color: '#1167e8' }} /> Your Comment
                    </h6>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Enter your comment / reason..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      style={{ borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: '0.88rem' }}
                    />
                    <div className="d-flex gap-2 mt-3">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleAction(1)}
                        disabled={disabled}
                        style={{ background: '#059669', borderColor: '#059669', borderRadius: 8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <IconCheck size={18} /> Approve
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleAction(2)}
                        disabled={disabled}
                        style={{ borderRadius: 8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <IconX size={18} /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ApproveLeave
